import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';

type FormData = {
  email: string;
  password: string;
};

type LoginFormProps = {
  form: FormData;
  setForm: React.Dispatch<React.SetStateAction<FormData>>;
  error: string | null;
  /** True while the sign-in request is in flight. */
  pending: boolean;
  onSubmit: (event: React.FormEvent) => void;
};

/**
 * Sign-in form UI.
 *
 * Extracted from `LoginPage` to keep each function under the line limit.
 *
 * The form asks for a REAL credential. There is no "any email / any password"
 * affordance and no skip link: a sign-in screen that accepts anything is not a
 * sign-in screen, and a "skip" link that only bounces back to the login page
 * teaches the operator that the controls here are decorative.
 *
 * The submit button reports that it is working. A credential round trip is not
 * instant, and a button that can be pressed twice sends two sign-in requests —
 * which the provider rate-limits.
 */
export function LoginForm({
  form,
  setForm,
  error,
  pending,
  onSubmit,
}: LoginFormProps): ReactNode {
  return (
    <form onSubmit={onSubmit} className="w-full max-w-md space-y-4">
      <h1 className="text-2xl font-bold text-content">Sign in</h1>
      <p className="text-content-muted">Mana Locals Admin</p>
      {error ? (
        <p role="alert" className="text-sm text-critical">
          {error}
        </p>
      ) : null}
      <CredentialField
        id="login-email"
        label="Email"
        type="email"
        autoComplete="username"
        placeholder="you@example.com"
        value={form.email}
        disabled={pending}
        onChange={(email) => setForm({ ...form, email })}
      />
      <CredentialField
        id="login-password"
        label="Password"
        type="password"
        autoComplete="current-password"
        placeholder="••••••••"
        value={form.password}
        disabled={pending}
        onChange={(password) => setForm({ ...form, password })}
      />
      <Button type="submit" variant="primary" className="w-full" disabled={pending}>
        {pending ? 'Signing in…' : 'Sign in'}
      </Button>
      <p className="text-sm text-content-muted text-center">
        Sessions end when you sign out or when the server expires them.
      </p>
    </form>
  );
}

/** One labelled credential input. */
function CredentialField({
  id,
  label,
  type,
  autoComplete,
  placeholder,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  type: 'email' | 'password';
  autoComplete: string;
  placeholder: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}): ReactNode {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-content" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        name={label.toLowerCase()}
        className="w-full rounded-control border border-border bg-input px-3 py-2 text-content"
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required
        disabled={disabled}
      />
    </div>
  );
}
